// Los roles de banco se detectan por NOMBRE, no por id: sus ids difieren entre
// ambientes (ver useBancoRoles en hooks/usePortalBancos/useBancoEquipo.ts).
// Compartido con el gate de rutas para que ambos usen el mismo criterio.
import { isRolDeBanco, isRolDeNotaria } from './routeAccess';
import { ROLES_PORTAL_AGENTES } from './multiRol';
import { esRolSupervisorInmobiliaria } from './supervisorInmobiliaria';
import {
  CURRENT_PORTAL_SUBDOMAIN,
  PORTAL_ROUTE_PREFIX,
  PORTAL_SUBDOMAIN_KEYS,
  type PortalSubdomain,
} from './portalUrls';

// Roles con acceso real al Portal Agente. Misma lista que usa
// AgentPortalImpersonationSelector: 1 Super Admin, 2 Admin Proyecto,
// 3 Agente Inmobiliario, 9 Agente Interno, 30 Super Admin "fake" de prod.
const AGENT_PORTAL_ROLE_IDS = [1, 2, 3, 9, 30];
const ROL_INMOBILIARIA = 4;
const ROL_CLIENTE = 23;
const ROL_SOCIO = 36;


export interface PortalHostAccessInput {
  rolId: number | null | undefined;
  rolNombre: string | null | undefined;
  /** allowedPaths de useAllowedMenus. Para Super Admin es el wildcard {'*'}, por eso isSuperAdmin va aparte. */
  allowedPaths: Set<string>;
  isSuperAdmin: boolean;
  /** useHasEmbajadorRole: null mientras consulta user_roles. */
  hasEmbajadorRole: boolean | null;
  /** useRolesAdicionales().hasSocioRole: rol 36 principal o adicional en user_roles; null mientras consulta. */
  hasSocioRole: boolean | null;
  /**
   * useRolesAdicionales().roles: rol_id activos en `user_roles` (null mientras
   * consulta). Opcional: agentes/clientes aceptan su rol aunque sea ADICIONAL
   * (multi-rol), no solo el activo. Normalmente el login ya lo activó con
   * `seleccionar_rol_activo`; esto cubre la BD sin esa RPC y las sesiones que
   * ya estaban abiertas.
   */
  rolesAsignados?: number[] | null;
  /** ¿Tiene algún menú que NO sea portal? Misma señal que useCanReturnToAdmin. */
  hasAdminMenus: boolean;
}

export interface PortalHostAccess {
  /** Subdominio de portal abierto, o null en admin.sozu.com / localhost. */
  currentPortal: PortalSubdomain | null;
  /** true si no hay subdominio que restringir, o si el usuario sí tiene acceso a él. */
  hasAccessToCurrentPortal: boolean;
  /** Portales con subdominio propio a los que el usuario sí puede entrar (para ofrecerle salida). */
  accessiblePortals: PortalSubdomain[];
  /** Señal para ofrecer el link cross-host al admin, sin el corte por subdominio de useCanReturnToAdmin. */
  canGoToAdmin: boolean;
}

/**
 * ¿El usuario tiene acceso al portal cuyo subdominio está abierto?
 *
 * Existe porque el aislamiento por subdominio de App.tsx es de *router*, no de
 * autorización: en agentes.sozu.com solo se montan las rutas /admin/agent/*, y
 * cualquier otra la absorbe el catch-all. Eso deja dos huecos que esta función
 * cierra junto con el gate de PermissionRoute:
 *
 *   1. Si PermissionRoute redirige a getFirstAllowedPath() y esa ruta pertenece
 *      a otro portal, no existe en este host → el catch-all la rebota →
 *      PermissionRoute vuelve a redirigir: loop de redirección.
 *   2. PermissionRoute abre /admin/agent/* y /admin/portal-cliente* a cualquier
 *      rol autenticado, así que un embajador puro entraba al portal de agentes
 *      con solo abrir agentes.sozu.com.
 *
 * El criterio de acceso es una UNIÓN PERMISIVA de las señales que el código ya
 * usaba en otros lados (permiso en BD bajo el prefijo del portal, o el rol_id
 * asociado a ese portal). La intención es rechazar a quien claramente no
 * pertenece sin dejar fuera a nadie que hoy sí entra — varios roles de portal
 * dependían del bypass de PermissionRoute y pueden no tener filas propias en
 * submenus_permisos.
 *
 * Es función pura y no hook a propósito: useAllowedMenus / useDynamicMenus
 * hacen sus fetches con useState+useEffect (sin react-query), así que volver a
 * invocarlos desde otro hook duplicaría las consultas. Quien la llama ya tiene
 * esos datos y se los pasa.
 */
export function computePortalHostAccess(input: PortalHostAccessInput): PortalHostAccess {
  const { rolId, rolNombre, allowedPaths, isSuperAdmin, hasEmbajadorRole, hasSocioRole, hasAdminMenus } = input;
  const rolesAsignados = input.rolesAsignados ?? [];
  const tieneAsignado = (ids: readonly number[]): boolean => rolesAsignados.some((r) => ids.includes(r));

  // allowedPaths es un Set<string>: iterar, no usar Array.some.
  const hasPathUnder = (prefix: string): boolean => {
    for (const p of allowedPaths) {
      if (p.startsWith(prefix)) return true;
    }
    return false;
  };

  const canAccessPortal = (portal: PortalSubdomain): boolean => {
    if (isSuperAdmin) return true;
    if (hasPathUnder(PORTAL_ROUTE_PREFIX[portal])) return true;

    switch (portal) {
      case 'agentes':
        return (
          AGENT_PORTAL_ROLE_IDS.includes(rolId ?? -1) ||
          // Solo los roles de agente como adicional; 1/2/30 cuentan como activos.
          tieneAsignado(ROLES_PORTAL_AGENTES)
        );
      case 'inmobiliarias':
        // Supervisor Inmobiliaria por nombre (su id difiere entre ambientes).
        return rolId === ROL_INMOBILIARIA || esRolSupervisorInmobiliaria(rolNombre);
      case 'clientes':
        return rolId === ROL_CLIENTE || rolNombre === 'Cliente' || tieneAsignado([ROL_CLIENTE]);
      case 'embajadores':
        // Mismos bypass por rol que el gate de portal-embajador en
        // PORTAL_GATES (lib/routeAccess.ts). El permiso en BD ya se resolvió
        // arriba con hasPathUnder().
        // ROL_CLIENTE (23): todo cliente entra al portal de Embajadores por rol.
        return (
          rolId === 1 ||
          rolId === 2 ||
          rolId === ROL_CLIENTE ||
          rolNombre === 'Embajador' ||
          rolNombre === 'Cliente' ||
          hasEmbajadorRole === true
        );
      case 'bancos':
        return isRolDeBanco(rolNombre);
      case 'notarios':
        // Mismo criterio por nombre que bancos: el rol Notario es 6 en dev pero
        // los ids de los roles de notaría no son estables entre ambientes.
        return isRolDeNotaria(rolNombre);
      case 'socios':
        // Mismo criterio que el gate de /admin/portal-socio (PORTAL_GATES): rol 36
        // principal o como rol adicional en user_roles (fideicomisarios, cuyo rol
        // principal suele ser Cliente). El permiso en BD ya lo resolvió hasPathUnder().
        return rolId === ROL_SOCIO || hasSocioRole === true;
      case 'condominios':
        // Sin bypass por rol: entra quien tiene permiso en BD bajo /admin/portal-condominio
        // (menú 30), que ya resolvió hasPathUnder().
        return false;
      default:
        return false;
    }
  };

  return {
    currentPortal: CURRENT_PORTAL_SUBDOMAIN,
    hasAccessToCurrentPortal:
      CURRENT_PORTAL_SUBDOMAIN === null || canAccessPortal(CURRENT_PORTAL_SUBDOMAIN),
    accessiblePortals: PORTAL_SUBDOMAIN_KEYS.filter((p) => canAccessPortal(p)),
    canGoToAdmin: isSuperAdmin || hasAdminMenus,
  };
}
